import type * as React from "react";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import { api } from "../api";
import type { BootstrapResponse, SettlementRecord, Stage } from "../types";
import { compactPerformanceFromPoints, type SettlementPerformance } from "../settlementPerformance";
import type { MonthRange } from "./PeriodFilter";
import { Button } from "./ui/Button";
import { Field, Input } from "./ui/Field";
import { EmptyState } from "./ui/Surface";
import { Modal } from "./ui/Modal";
import "./ui/Chip.css";
import "./WorkerPerformance.css";

const money = (value: number) => `${value.toLocaleString("ko-KR")}원`;
/* 타일에 적는 짧은 금액. 축의 눈금과 달리 소수점을 두지 않는다 — 눈금은 네 칸이 고르게
   올라야 해서 3548.5가 필요하지만, 타일은 값 하나라 "3,548만원"이면 충분하고 소수점이
   붙으면 큰 글자에서 그 점 하나가 먼저 눈에 걸린다. 자릿수는 쉼표로 끊는다. */
const shortMoney = (value: number) => {
  if (value >= 1e8) return `${Math.round(value / 1e8 * 10) / 10}억원`;
  if (value >= 1e4) return `${Math.round(value / 1e4).toLocaleString("ko-KR")}만원`;
  return money(value);
};

export type PerformanceGranularity = "day" | "week" | "biweek" | "week4";
type PerformanceRange = { start: string; end: string } | null;
type PerformanceBucket = { start: number; end: number; label: string };

const performanceGranularityDays: Record<PerformanceGranularity, number> = { day: 1, week: 7, biweek: 14, week4: 28 };
const performanceGranularityLabels: Record<PerformanceGranularity, string> = { day: "일", week: "주", biweek: "2주", week4: "4주" };
/* 고른 칸은 연도까지 적는다 — "9/23"만 있으면 올해인지 작년인지가 그림 어디에도 없다.
   칸이 하루보다 잘면 시각까지 적는다: 하루가 180칸으로 쪼개진 화면에서 날짜만 적으면
   옆 칸과 같은 글자가 되어 무엇을 짚었는지 알 수 없다. */
const pickedLabel = (bucket: { start: number; end: number }) => {
  const day = new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit" })
    .format(new Date(bucket.start)).replace(/\.$/, "").replace(/\s/g, "");
  if (bucket.end - bucket.start >= 86_400_000) return day;
  const time = new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", hour: "2-digit", minute: "2-digit", hour12: false })
    .format(new Date(bucket.start));
  return `${day} ${time}`;
};
const performancePeriodOptions = [{ days: 7, label: "1주" }, { days: 14, label: "2주" }, { days: 28, label: "4주" }, { days: 56, label: "8주" }, { days: 84, label: "12주" }, { days: 168, label: "24주" }];
const dayMs = 86_400_000;
const performanceDayMs = 86_400_000;
const performanceDateFormatter = new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", month: "numeric", day: "numeric" });
/* 점 세 개짜리 "9. 1." 보다 "9/1"이 읽힌다 — 마침표가 숫자 사이에 끼면 어디까지가 한 날인지
   눈으로 끊어야 한다. */
const performanceShortDate = (ms: number) => {
  const key = performanceDateKey(new Date(ms));
  return `${Number(key.slice(5, 7))}/${Number(key.slice(8, 10))}`;
};
/* 포매터를 부를 때마다 새로 만들면 그 생성 자체가 서식보다 비싸다 — 정산 줄 삼천 개를
   하루 단위로 묶는 자리에서 삼천 번을 만들었고, 그래프에 손을 얹을 때마다 그 일이 처음부터
   다시 돌았다. 한 번 만들어 두고 쓴다. */
const seoulDate = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul" });
const performanceDateKey = (date: Date) => seoulDate.format(date);
const performanceDateMs = (date: string) => Date.parse(`${date}T00:00:00+09:00`);

type PerformanceRecord = {
  id: string;
  workerId: string;
  workerName: string;
  projectName: string;
  subtaskName: string;
  stage: Stage | null;
  completedAt: string;
  completedMs: number;
  normal: number;
  unitPrice: number;
  amount: number;
  recordCount: number;
};

export const performanceRecordsFromSettlements = (
  settlements: SettlementRecord[],
  stageBySubtask: ReadonlyMap<string, Stage>,
): PerformanceRecord[] => settlements.flatMap((record) => {
  if (record.source !== "piecework" || record.recordKind !== "base") return [];
  return [{
    id: record.id,
    workerId: record.workerId,
    workerName: record.workerName,
    projectName: record.projectName ?? "프로젝트",
    subtaskName: record.subtaskName ?? record.title,
    stage: record.subtaskId ? stageBySubtask.get(record.subtaskId) ?? null : null,
    completedAt: `${record.settlementDate}T00:00:00+09:00`,
    /* 원장 생성 시각은 이관·재처리 시점일 수 있다. 성과 기간은 확정된 정산 기준일로 집계한다. */
    completedMs: performanceDateMs(record.settlementDate),
    normal: record.quantity ?? 0,
    unitPrice: record.unitAmount ?? 0,
    amount: record.grossAmount,
    recordCount: 1,
  }];
});

export function buildPerformanceBuckets(granularity: PerformanceGranularity, periodDays: number, customRange: PerformanceRange) {
  const bucketDays = performanceGranularityDays[granularity];
  const todayMs = performanceDateMs(performanceDateKey(new Date()));
  const start = customRange ? performanceDateMs(customRange.start) : todayMs + performanceDayMs - periodDays * performanceDayMs;
  const end = customRange ? performanceDateMs(customRange.end) + performanceDayMs : todayMs + performanceDayMs;
  const buckets: PerformanceBucket[] = [];
  /* 달 1일부터 7일씩 끊길 때만 "첫째 주"라고 부를 수 있다. 최근 24주처럼 아무 날에서 시작하는
     기간에서는 그 말이 틀리므로 날짜 범위를 그대로 쓴다. */
  const startDay = performanceDateKey(new Date(start));
  const monthWeeks = bucketDays === 7 && startDay.endsWith("-01");
  const ordinals = ["첫", "둘", "셋", "넷", "다섯", "여섯"];
  for (let cursor = start; cursor < end; cursor += bucketDays * performanceDayMs) {
    const bucketEnd = Math.min(end, cursor + bucketDays * performanceDayMs);
    const first = performanceShortDate(cursor);
    const last = performanceShortDate(bucketEnd - performanceDayMs);
    const index = Math.round((cursor - start) / (7 * performanceDayMs));
    const label = monthWeeks && ordinals[index]
      ? `${Number(startDay.slice(5, 7))}월 ${ordinals[index]}째 주`
      : bucketDays === 1 || first === last ? first : `${first}–${last}`;
    buckets.push({ start: cursor, end: bucketEnd, label });
  }
  return buckets;
}

/** 세로축 눈금. 돈이라는 말이 어디에도 없으면 "200만"이 무엇의 200만인지 알 수 없다. */
const formatPerformanceAxisValue = (value: number) => `${formatPerformanceAxisNumber(value)}원`;
const formatPerformanceAxisNumber = (value: number) => {
  for (const [divisor, unit] of [[1e12, "조"], [1e8, "억"]] as const) {
    if (value >= divisor) return `${Math.round(value / divisor * 10) / 10}${unit}`;
  }
  if (value >= 10_000) {
    const tenThousands = value / 10_000;
    return `${Number.isInteger(tenThousands) ? tenThousands : Math.round(tenThousands * 10) / 10}만`;
  }
  return Math.round(value).toLocaleString("ko-KR");
};

/* 축 꼭대기는 가장 큰 막대 바로 위에 둔다. 눈금 사이가 성길수록 남는 여백이 커져, 105만짜리
   막대가 200만 축 아래에서 절반 키로 서 있게 된다 — 그림이 데이터보다 여백을 더 보여 준다.
   네 칸으로 나눠 떨어지는 단계만 쓴다: 1.2 → 30·60·90·120만처럼 읽히는 눈금이 나온다. */
const performanceNiceMax = (value: number) => {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  return ([1, 1.2, 1.6, 2, 2.4, 3, 4, 5, 6, 8, 10].find((step) => power * step >= value) ?? 10) * power;
};

/* 부드러운 곡선. 레퍼의 선은 꺾인 선이 아니라 단조 3차 보간이다 — 점과 점 사이를 곧게
   이으면 날마다 꺾여 톱니가 되고, 보통 스플라인을 쓰면 두 점 사이에서 값이 양 끝보다
   커지거나 작아져(오버슈트) 없던 봉우리가 생긴다. 단조 보간은 그 일이 없다: 오르는 구간은
   오르기만 하고 내리는 구간은 내리기만 한다. 돈과 개수를 그리는 그림이라 이 성질이 필요하다. */
const smoothPath = (points: Array<{ x: number; y: number }>) => {
  if (points.length === 0) return "";
  if (points.length === 1) return `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
  /* 각 점의 기울기를 Fritsch–Carlson 방식으로 고른다 — 이웃한 두 기울기의 부호가 다르면
     그 점은 봉우리나 골짜기이므로 기울기를 0으로 눕혀 곡선이 넘어가지 않게 한다. */
  const n = points.length;
  const slopes: number[] = new Array(n).fill(0);
  const deltas: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    const dx = points[i + 1].x - points[i].x;
    deltas.push(dx === 0 ? 0 : (points[i + 1].y - points[i].y) / dx);
  }
  slopes[0] = deltas[0];
  slopes[n - 1] = deltas[n - 2];
  for (let i = 1; i < n - 1; i += 1) {
    const left = deltas[i - 1], right = deltas[i];
    slopes[i] = left * right <= 0 ? 0 : (left + right) / 2;
    /* 기울기가 구간 기울기의 세 배를 넘으면 그 구간에서 곡선이 부풀어 오른다 — 잘라 둔다. */
    const limit = 3 * Math.min(Math.abs(left), Math.abs(right));
    if (Math.abs(slopes[i]) > limit) slopes[i] = Math.sign(slopes[i]) * limit;
  }
  let path = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
  for (let i = 0; i < n - 1; i += 1) {
    const dx = (points[i + 1].x - points[i].x) / 3;
    path += ` C${(points[i].x + dx).toFixed(1)},${(points[i].y + slopes[i] * dx).toFixed(1)}`
      + ` ${(points[i + 1].x - dx).toFixed(1)},${(points[i + 1].y - slopes[i + 1] * dx).toFixed(1)}`
      + ` ${points[i + 1].x.toFixed(1)},${points[i + 1].y.toFixed(1)}`;
  }
  return path;
};

/** 짧게 줄인 수. 축에 "3,419"를 그대로 적으면 눈금 글자가 그림보다 넓어진다. */
const shortCount = (value: number) => value >= 10_000 ? `${Math.round(value / 1000)}k` : value >= 1000 ? `${(value / 1000).toFixed(1)}k` : `${Math.round(value)}`;

export function PerformanceChart({ buckets, values, counts, compare, individual, onPick, picked }: {
  buckets: PerformanceBucket[]; values: number[]; individual: boolean;
  /** 같은 칸의 작업 개수. 돈과 나란히 그려 "많이 해서 번 것인지 비싼 걸 해서 번 것인지"가 갈린다. */
  counts?: number[];
  /** 같은 길이의 바로 앞 기간. 뒤에 연회색으로 깔아 견줄 자가 된다. */
  compare?: number[];
  /** 막대를 누르면 그 구간이 무엇이었는지 위에서 말한다 — 눈금을 고르는 손잡이를 없앤 대신
      "이 막대가 얼마냐"는 질문을 막대 자신이 받는다. */
  onPick?: (index: number) => void; picked?: number | null;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 760, height: 280 });
  const descriptionId = useId();
  /* 그리기 전에 잰다(useLayoutEffect) — 전에는 가정한 760×280 으로 한 번 그린 뒤 재서 다시 그려, 성과 칸을 열 때마다 그림이 가운데서 한 박자(0.1초) 늘어났다(2026-10-21 지윤). */
  useLayoutEffect(() => {
    const element = host.current!;
    const measure = () => {
      const width = Math.max(1, element.clientWidth), height = Math.max(1, element.clientHeight);
      setSize(current => current.width === width && current.height === height ? current : { width, height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const { width, height } = size;
  /* 좁은 카드에서는 축을 걷는다 — 눈금 글자가 그림보다 넓은 자리를 먹고, 값은 막대를 눌러
     머리줄에서 읽는다. 시세 화면과 같은 규칙이다. */
  const bare = !!onPick;
  /* 좁은 카드의 치수는 레퍼의 선 차트를 재서 가져왔다 — 왼쪽에 개수 눈금, 오른쪽에 돈 눈금,
     아래에 날짜. 두 축이 서로 다른 단위라 자리를 양쪽으로 갈라야 어느 선이 어느 자를 쓰는지
     헷갈리지 않는다. */
  /* 왼쪽 자리는 62다 — "5,000원"처럼 쉼표가 들어간 눈금이 44에서는 앞글자가 잘렸다. */
  const padding = bare ? { left: 48, right: 8, top: 14, bottom: 26 } : { left: 78, right: 16, top: 24, bottom: 40 };
  const chartWidth = Math.max(1, width - padding.left - padding.right);
  const chartHeight = Math.max(1, height - padding.top - padding.bottom);
  /* 두 벌이 한 눈금을 쓴다 — 각자 제 꼭대기에 맞추면 높이가 뜻을 잃는다. */
  const maximum = performanceNiceMax(Math.max(
    values.reduce((maximum, value) => Math.max(maximum, value), 0),
    (compare ?? []).reduce((maximum, value) => Math.max(maximum, value), 0)));
  const slot = chartWidth / Math.max(1, buckets.length);
  const x = (index: number) => padding.left + slot * (index + .5);
  const y = (value: number) => padding.top + chartHeight - value / maximum * chartHeight;
  const points = values.flatMap((value, index) => value > 0 ? [{ x: x(index), y: y(value), value, index }] : []);
  const linePath = points.map((point, index) => `${index ? "L" : "M"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
  const baseline = padding.top + chartHeight;
  /* 값이 0인 구간까지 포함한 선. 0을 건너뛰면 일하지 않은 날이 그림에서 사라져 흐름이 끊긴다. */
  const fullPoints = values.map((value, index) => ({ x: x(index), y: y(value), value, index }));
  const fullLine = fullPoints.map((point, index) => `${index ? "L" : "M"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
  const fullArea = fullPoints.length ? `M${fullPoints[0].x.toFixed(1)},${padding.top + chartHeight} ${fullPoints.map(point => `L${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ")} L${fullPoints.at(-1)!.x.toFixed(1)},${padding.top + chartHeight} Z` : "";
  const areaPath = points.length ? `M${points[0].x.toFixed(1)},${baseline} ${points.map(point => `L${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ")} L${points.at(-1)!.x.toFixed(1)},${baseline} Z` : "";
  const labelEvery = Math.max(1, Math.ceil(buckets.length / Math.max(2, Math.floor(chartWidth / 100))));
  const labelIndices = buckets.map((_, index) => index).filter(index => index % labelEvery === 0 && index < buckets.length - labelEvery);
  if (buckets.length) labelIndices.push(buckets.length - 1);
  /* 좁은 카드에서는 막대를 얇게 세운다 — 두꺼우면 막대 몇 개가 그림이 아니라 덩어리로
     보이고, 시세 화면처럼 흐름을 읽는 그림이 되지 않는다. */
  const barWidth = Math.min(30, slot * .72);
  /* 두께는 고정이다. 칸 수를 데이터가 정하게 두면 기간을 바꿀 때마다 막대가 굵어졌다
     얇아져서 같은 그림으로 안 읽힌다 — 두께를 못박고 칸 수를 폭에 맞춘다.
     칸 하나가 2px보다 넓어지면 막대도 그만큼 굵혀 그림이 폭을 꽉 채우게 한다 — 얇게만
     고정하면 오른쪽이 비어 보인다. */
  /* 막대 사이에는 틈이 있다. 한때 서로 붙여 한 덩어리 실루엣으로 그렸는데, 그러면 값이 0인
     날과 아주 적은 날이 같은 바닥면이 되어 "쉰 날"이 보이지 않았다. 떨어뜨려 두면 바닥에
     점만 남은 자리가 곧 쉰 날이고, 그 점들이 줄지어 있는 모양이 "이 기간이 어땠나"를 덩어리
     보다 더 잘 말한다.
     두께는 고정이다 — 칸 수를 자료가 정하게 두면 기간을 바꿀 때마다 막대가 굵어졌다 얇아져서
     같은 그림으로 안 읽힌다. 칸이 아무리 넓어져도 3px을 넘지 않는다. */
  const tickWidth = bare ? Math.max(2, Math.min(3, slot * .4)) : 1;
  /* 칸 가운데에 세운다 — 왼쪽에 붙이면 막대와 막대 사이 틈이 한쪽으로 쏠려 격자가 기운다. */
  const tickOffset = bare ? (slot - tickWidth) / 2 : -.4;
  /* 값이 0인 칸은 바닥에 점만 남긴다. 빼면 막대 수가 자료 수를 따라 들쭉날쭉해지고, 바닥의
     점줄이 끊겨 "그날은 없었다"가 아니라 "그림이 비었다"로 보인다. */
  const stub = bare ? 2 : 1;
  return <div className="performance-plot">
    <div ref={host} className={individual ? "performance-area-chart" : "performance-chart"} role="img" aria-label="기간별 작업자 성과 차트" aria-describedby={descriptionId}>
      <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        {!bare && Array.from({ length: 5 }, (_, index) => {
          const value = maximum * index / 4;
          return <g key={index}><line className="performance-area-grid" x1={padding.left} y1={y(value)} x2={padding.left + chartWidth} y2={y(value)} /><text className="performance-area-axis" x={padding.left - 10} y={y(value) + 5} textAnchor="end">{formatPerformanceAxisValue(value)}</text></g>;
        })}
        {bare ? (() => {
          /* 두 선이 각자의 자를 쓴다 — 돈과 개수는 단위가 달라 한 자에 올리면 작은 쪽이
             바닥에 눌려 흐름이 안 보인다. 왼쪽 자가 개수, 오른쪽 자가 돈이다. */
          /* 개수가 한 칸도 없으면 그 선은 그리지 않는다 — 바닥에 눌린 평평한 선과 0만
             적힌 눈금은 "자료가 없다"가 아니라 "일을 하나도 안 했다"로 읽힌다. 그때는 돈
             선 하나만 남고, 그 눈금이 왼쪽 자리를 받는다. */
          const seriesCounts = counts ?? [];
          const hasCounts = seriesCounts.some(value => value > 0);
          const countMax = performanceNiceMax(seriesCounts.reduce((top, value) => Math.max(top, value), 0));
          const yMoney = (value: number) => padding.top + chartHeight - (maximum ? value / maximum : 0) * chartHeight;
          const yCount = (value: number) => padding.top + chartHeight - (countMax ? value / countMax : 0) * chartHeight;
          const px = (index: number) => padding.left + (buckets.length <= 1 ? chartWidth / 2 : chartWidth * index / (buckets.length - 1));
          const moneyPoints = values.map((value, index) => ({ x: px(index), y: yMoney(value) }));
          const countPoints = seriesCounts.map((value, index) => ({ x: px(index), y: yCount(value) }));
          const moneyLine = smoothPath(moneyPoints);
          const countLine = smoothPath(countPoints);
          /* 개수 선 아래만 면을 깐다 — 둘 다 채우면 겹친 자리의 농도가 두 배가 되어 없는
             세 번째 값처럼 보인다. 레퍼도 한 선만 채운다. */
          /* 선 하나뿐일 때는 그 아래를 채운다 — 레퍼의 Volume 그림이 그렇다. 선만 있으면
             그림이 가늘어 카드 안에서 눈에 안 걸린다. */
          const moneyArea = moneyPoints.length
            ? `${moneyLine} L${moneyPoints.at(-1)!.x.toFixed(1)},${baseline.toFixed(1)} L${moneyPoints[0].x.toFixed(1)},${baseline.toFixed(1)} Z`
            : "";
          const countArea = countPoints.length
            ? `${countLine} L${countPoints.at(-1)!.x.toFixed(1)},${baseline.toFixed(1)} L${countPoints[0].x.toFixed(1)},${baseline.toFixed(1)} Z`
            : "";
          const ticks = 4;
          /* 날짜는 넷만 적는다 — 레퍼가 그 수로 적고, 그보다 촘촘하면 글자가 서로 붙는다. */
          const labelStep = Math.max(1, Math.ceil((buckets.length - 1) / 3));
          return <>
            {/* 면은 선 아래에서 아래로 옅어진다 — 잰 값 그대로 흰빛 24% → 9% → 0%. 한 겹으로
                채우면 바닥 쪽이 무거워 선보다 면이 먼저 읽힌다. */}
            <defs>
              <linearGradient id={`${descriptionId}-fill`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-ink)" stopOpacity="0.24" />
                <stop offset="45%" stopColor="var(--color-ink)" stopOpacity="0.09" />
                <stop offset="100%" stopColor="var(--color-ink)" stopOpacity="0" />
              </linearGradient>
            </defs>
            {Array.from({ length: ticks + 1 }, (_, index) => {
              const ratio = index / ticks;
              const lineY = padding.top + chartHeight - ratio * chartHeight;
              return <g key={index}>
                <line className="performance-line-grid" x1={padding.left} y1={lineY} x2={padding.left + chartWidth} y2={lineY} />
                {hasCounts && <text className="performance-line-axis" x={padding.left - 8} y={lineY + 4} textAnchor="end">{shortCount(countMax * ratio)}</text>}
                <text className={`performance-line-axis performance-line-axis--money${hasCounts ? "" : " performance-line-axis--left"}`}
                  /* 왼쪽 눈금 숫자는 그림 왼쪽 끝에 맞춰 왼쪽 정렬 — Claude Reflect 그대로(링크 실측: 숫자 x = 그림 x, text-anchor start). */
                  x={hasCounts ? padding.left + chartWidth + 8 : 0} y={lineY + 4}
                  textAnchor="start">{formatPerformanceAxisNumber(maximum * ratio)}</text>
              </g>;
            })}
            {hasCounts && countArea && <path className="performance-line-area" d={countArea} />}
            {hasCounts && countLine && <path className="performance-line-count" d={countLine} />}
            {!hasCounts && moneyArea && <path d={moneyArea} fill={`url(#${descriptionId}-fill)`} stroke="none" />}
            {moneyLine && <path className="performance-line-money" d={moneyLine} />}
            {buckets.map((bucket, index) => index % labelStep === 0 || index === buckets.length - 1
              ? <text className="performance-line-date" key={index} x={px(index)} y={baseline + 18}
                  textAnchor={index === 0 ? "start" : index === buckets.length - 1 ? "end" : "middle"}>{bucket.label}</text>
              : null)}
            {/* 짚은 자리는 세로선 하나와 두 선 위의 점이다 — 선 차트에서 "어느 날"은 가로
                한 자리이므로, 그 자리를 세로로 그으면 두 값이 같은 날의 것임이 보인다. */}
            {picked != null && picked >= 0 && buckets[picked] && <>
              <line className="performance-pick-line" x1={px(picked)} y1={padding.top} x2={px(picked)} y2={baseline} />
              {hasCounts && countPoints[picked] && <circle className="performance-line-dot performance-line-dot--count" cx={countPoints[picked].x} cy={countPoints[picked].y} r="3" />}
              {moneyPoints[picked] && <>
                <circle className="performance-line-dot performance-line-dot--money" cx={moneyPoints[picked].x} cy={moneyPoints[picked].y} r="3" />
              </>}
            </>}
            <rect x={padding.left} y={padding.top} width={chartWidth} height={chartHeight} fill="transparent"
              onMouseMove={event => { const box = event.currentTarget.getBoundingClientRect();
                const index = Math.max(0, Math.min(buckets.length - 1, Math.round((event.clientX - box.left) / Math.max(1, box.width) * (buckets.length - 1))));
                /* 같은 칸 안에서 움직이는 동안에는 알리지 않는다 — 픽셀마다 알리면 한 칸을
                   지나는 사이에 화면을 수십 번 다시 그린다. */
                if (index !== picked) onPick?.(index); }}
              onMouseLeave={() => { if (picked !== -1) onPick?.(-1); }} />
          </>;
        })() : individual ? <>
          {areaPath && <path className="performance-area-fill" d={areaPath} />}
          {linePath && <path className="performance-area-line" d={linePath} />}
          {points.map(point => <circle key={point.index} className="performance-area-point" cx={point.x} cy={point.y} r="4"><title>{buckets[point.index].label + " · " + money(point.value)}</title></circle>)}
        </> : points.map(point => <rect className="performance-bar" key={point.index}
          x={point.x - barWidth / 2} y={point.y} width={barWidth} height={baseline - point.y} rx={Math.min(4, barWidth / 2)}
          ><title>{buckets[point.index].label + " · " + money(point.value)}</title></rect>)}
        {/* 고른 막대 옆에 그 구간이 무엇이고 얼마였는지 적는다 — 머리줄에 올리면 총합과
            같은 자리에 다른 뜻의 숫자가 번갈아 서서 어느 쪽을 보고 있는지 흐려진다. */}
        {/* 짚은 날의 쪽지. 레퍼 그대로 날짜 한 줄 아래 계열마다 한 줄이고, 줄마다 제 색의
            짧은 선과 이름이 왼쪽, 값이 오른쪽 끝에 선다 — 값끼리 세로로 읽힌다. */}
        {/* 짚은 날의 쪽지. 레퍼 그대로 — 모서리 12, 날짜 한 줄(11 흐림) 아래에 점 하나와
            큰 값(20). 계열 이름은 적지 않는다: 점 색이 어느 선인지 이미 말하고, 좁은 쪽지에서
            이름까지 넣으면 값이 작아진다. */}
        {bare && picked != null && picked >= 0 && buckets[picked] && (() => {
          const pointX = padding.left + (buckets.length <= 1 ? chartWidth / 2 : chartWidth * picked / (buckets.length - 1));
          const when = pickedLabel(buckets[picked]);
          const showCount = (counts ?? []).some(value => value > 0);
          const rows = [
            ...(showCount ? [{ key: "count", value: (counts?.[picked] ?? 0).toLocaleString("ko-KR") }] : []),
            { key: "money", value: money(values[picked] ?? 0) },
          ];
          /* Reflect 쪽지 실측: 검정 판 모서리 8 · 안쪽 6/12 · 날짜 12/17 흐린 흰(75%) · 값 13/17 580 흰 ·
             짚은 세로선 한가운데 위. 점·이름은 적지 않는다 — 값 줄이 곧 그 선이다. */
          const charWidth = (text: string, size: number) => [...text].reduce((sum, char) => sum + (/[가-힣]/.test(char) ? size : size * 0.58), 0);
          const noteWidth = Math.ceil(Math.max(charWidth(when, 12), ...rows.map(row => charWidth(row.value, 13) * 1.04)) + 24);
          const noteHeight = 12 + 17 * (rows.length + 1) + 2 * rows.length;
          const noteX = Math.max(2, Math.min(width - noteWidth - 2, pointX - noteWidth / 2));
          const noteY = padding.top + 8;
          return <g className="performance-pick-card performance-pick-card--reflect">
            <rect x={noteX} y={noteY} width={noteWidth} height={noteHeight} rx="8" />
            <text className="performance-pick-when" x={noteX + noteWidth / 2} y={noteY + 6 + 13} textAnchor="middle">{when}</text>
            {rows.map((row, index) => <text key={row.key} className="performance-pick-amount" x={noteX + noteWidth / 2} y={noteY + 6 + 13 + (index + 1) * 19} textAnchor="middle">{row.value}</text>)}
          </g>;
        })()}
        {!bare && slot >= 64 && points.map(point => <text className="performance-area-value" key={point.index} x={point.x} y={point.y - 12 < padding.top + 10 ? point.y + 20 : point.y - 12} textAnchor="middle">{formatPerformanceAxisValue(point.value)}</text>)}
        {!bare && labelIndices.map(index => {
          const edge = slot < 100 && buckets.length > 1;
          const labelX = edge && index === 0 ? padding.left : edge && index === buckets.length - 1 ? padding.left + chartWidth : x(index);
          return <text className="performance-area-axis performance-time-label" key={index} x={labelX} y={baseline + 28} textAnchor={edge && index === 0 ? "start" : edge && index === buckets.length - 1 ? "end" : "middle"}>{buckets[index].label}</text>;
        })}
      </svg>
    </div>
    <div className="performance-accessible-data">
      <p id={descriptionId}>{buckets.length}개 기간 · 총 성과 {money(values.reduce((sum, value) => sum + value, 0))}. 기간별 성과 수치 표에서 모든 값을 확인할 수 있습니다.</p>
      <table aria-label="기간별 성과 수치"><thead><tr><th scope="col">기간</th><th scope="col">성과</th></tr></thead><tbody>{buckets.map((bucket, index) => <tr key={bucket.start}><th scope="row">{bucket.label}</th><td>{money(values[index])}</td></tr>)}</tbody></table>
    </div>
  </div>;
}

/**
 * 요약 한 줄이 정산 카드의 제목이다. 따로 카드를 세웠을 때는 "이 기간에 얼마가 나갔나"가
 * 정산과 별개의 화면처럼 보였고, 기간 칩과 표 사이에 끼어 읽는 순서를 끊었다. 차트는 이
 * 줄의 근거라 접어 두고, 작업자를 고르는 일은 표 옆으로 내렸다 — 좁히면 표와 이 줄이 같이
 * 좁아지므로, 고르는 자리는 좁아지는 것 옆이어야 한다.
 */
export function WorkerPerformance({ data, range, selectedWorkerId, compact = false, scaleExtra, headLead, headTail, onSelectWorker, projectName, subtaskName, stage, sectionTail, sectionTitle = "추이", hideChart = false, compactScale, reflectTitle, reflectPeriod }: {
  /** 설정 > 성과 = Claude Reflect 머리(제목 + 한 문장). 주면 숫자 타일 대신 이 머리가 선다. */
  reflectTitle?: string;
  /** Reflect 머리 문장의 기간 이름(「6개월」). */
  reflectPeriod?: string;
  /** 좁은 카드의 집계 단위를 밖에서 고른다(설정 창 성과 칸 제목 줄의 일|주|2주|4주). 주면 칸을 서른 개로 다시 묶지 않는다 — 고른 단위가 곧 한 점이다. */
  compactScale?: PerformanceGranularity;
  /** 그림 이름 줄 오른쪽에 서는 손잡이(Reflect 의 Conversations / Time spent 자리). */
  sectionTail?: React.ReactNode;
  /** 이름 줄 글자 — 아래 칸이 정산 표로 바뀌면 그 이름으로. */
  sectionTitle?: string;
  /** 그림 대신 다른 것(정산 표)을 볼 때 — 위 숫자 줄은 남긴다. */
  hideChart?: boolean;
  data: BootstrapResponse; range: MonthRange; selectedWorkerId: string;
  projectName?: string;
  subtaskName?: string;
  stage?: Stage;
  /** 좁은 카드에서는 평균 넷 대신 눈금 고르개와 그림 하나를 둔다 — 넷을 세로로 쌓으면
      값이 같은 말을 네 번 하고, 그림은 ⓘ 뒤에 숨어 한 번도 안 열린다. */
  compact?: boolean;
  /** 목록에서 한 사람을 고르면 그림이 그 사람 것으로 좁혀진다. */
  onSelectWorker?: (workerId: string) => void;
  /** 눈금 줄 오른쪽에 붙일 것. 기간 고르개가 여기 선다 — 머리줄은 읽는 줄이고 이 줄이
      "어떻게 볼지"를 쥐는 줄이라, 고르는 일은 전부 여기로 모인다. */
  scaleExtra?: ReactNode;
  /** 머리줄 맨 앞과 맨 끝. 배지와 손잡이가 총액과 한 줄에 서야 옆 카드들과 같은 머리줄이
      된다 — 밖에서 따로 그리면 줄이 둘로 갈린다. */
  headLead?: ReactNode;
  headTail?: ReactNode;
}) {
  const [rankingOpen, setRankingOpen] = useState(false);
  const peopleRef = useRef<HTMLDetailsElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const [listStyle, setListStyle] = useState<{ top: number; left: number } | undefined>(undefined);
  const openPeople = () => {
    const box = peopleRef.current?.querySelector("summary")?.getBoundingClientRect();
    if (!box) return;
    /* 실제 폭으로 재고, 감싼 창(설정 창) 오른쪽 안에서 멈춘다 — 창이 넘친 쪽을 자르기 때문이다. */
    const width = listRef.current?.offsetWidth || Math.min(288, window.innerWidth - 32);
    const bound = peopleRef.current?.closest(".settings-dialog")?.getBoundingClientRect().right ?? window.innerWidth;
    setListStyle({ top: box.bottom + 6, left: Math.max(16, Math.min(box.left, bound - width - 8)) });
  };
  /* 고른 막대. 그 구간이 얼마였는지는 머리줄이 대신 말한다. */
  const [picked, setPicked] = useState<number | null>(null);
  const [chartOpen, setChartOpen] = useState(false);
  const rankButton = useRef<HTMLButtonElement>(null), chartButton = useRef<HTMLButtonElement | null>(null);
  const [granularity, setGranularity] = useState<PerformanceGranularity>("day");
  type LoadedPerformance = { key: string; data: SettlementPerformance }
    | { key: string; points: Array<{ at: string; amount: number; workerId: string }> };
  const [loadedPerformance, setLoadedPerformance] = useState<LoadedPerformance | null>(null);
  const requestWorkerId = compact || selectedWorkerId === "all" ? undefined : selectedWorkerId;
  const requestProjectName = compact ? undefined : projectName;
  const requestSubtaskName = compact ? undefined : subtaskName;
  const requestStage = compact ? undefined : stage;
  const performanceKey = JSON.stringify([range?.from, range?.to, requestWorkerId, requestProjectName, requestSubtaskName, requestStage, compact]);
  const workerNames = useMemo(() => new Map(data.workers.map(worker => [worker.id, worker.displayedName || worker.name])), [data.workers]);
  // A changed server query renders before its effect. Never label old totals as
  // the new query. Compact worker selection remains local, with no extra request.
  const performance = useMemo(() => loadedPerformance?.key !== performanceKey ? null
    : "points" in loadedPerformance ? compactPerformanceFromPoints(loadedPerformance.points, workerNames, selectedWorkerId)
    : loadedPerformance.data, [loadedPerformance, performanceKey, workerNames, selectedWorkerId]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  /* 달 칩이 고른 범위를 그대로 본다. "전체"면 범위가 없으므로 최근 여섯 달 — 칩이 덮는
     구간과 같은 폭 — 을 본다. 화면에 없는 기간이 차트에만 나오면 숫자를 대조할 수 없다. */
  const customRange: PerformanceRange = range ? { start: range.from, end: range.to } : null;
  /* 기간을 안 골랐으면 최근 여섯 달을 본다. 다만 좁은 카드에서는 그 절반이 빈칸이면 막대가
     오른쪽 끝에 실오라기로 뭉쳐 아무것도 못 읽는다 — 값이 실제로 있는 날들만 본다. */
  const dataRange = performance?.firstDate && performance.lastDate
    ? { start: performance.firstDate, end: performance.lastDate } : null;
  const chartRange = customRange ?? (compact ? dataRange : null);
  /* 좁은 카드는 늘 하루가 막대 하나다. 더 잘게 쓰려면 "몇 시에 얼마를 벌었나"가 있어야
     하는데 정산 줄에는 날짜만 있다 — 시각을 쪼개면 하루치가 한 칸에 몰려 그림이 거짓이 된다.
     3달이든 1년이든 이미 충분히 촘촘해서 더 굵게 묶을 이유도 없다: 짚어서 읽으면 된다. */
  const scale = compact ? (compactScale ?? "day") : granularity;
  const buckets = useMemo(() => buildPerformanceBuckets(scale, 183, chartRange), [chartRange?.start, chartRange?.end, scale]);
  /* 좁은 카드에서는 막대가 늘 같은 굵기·같은 간격으로 서도록 칸을 폭에 맞춰 다시 나눈다.
     하루가 여러 칸이 되면 그 하루의 값을 칸마다 쪼개 놓지 않고, 값이 있는 칸에만 얹는다. */
  /* 받아 올 구간은 고른 기간 하나만 보고 정한다. 그리는 구간(chartRange)을 그대로 쓰면
     고리가 생긴다 — 자료를 받아 오면 그 자료의 첫날·마지막날로 그리는 구간이 바뀌고, 구간이
     바뀌면 다시 받아 오고, 받아 오면 또 바뀐다. "불러오는 중"이 끝나지 않고 그 사이 화면이
     계속 다시 그려져 그래프에 얹은 값까지 느려졌다. 받는 쪽은 자기가 만든 값을 안 본다. */
  const fetchBounds = useMemo(() => {
    const window = buildPerformanceBuckets(scale, 183, customRange);
    return { start: window[0]?.start, end: window.at(-1)?.end };
  }, [customRange?.start, customRange?.end, scale]);
  const rangeStart = fetchBounds.start;
  const rangeEnd = fetchBounds.end;
  useEffect(() => {
    if (rangeStart === undefined || rangeEnd === undefined) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    setLoadedPerformance(null);
    const from = performanceDateKey(new Date(rangeStart));
    const to = performanceDateKey(new Date(rangeEnd - performanceDayMs));
    // Compact keeps its established financial scope. Only the full ledger view
    // replaces its paginated base-piecework reads with the aggregate endpoint.
    const pending: Promise<LoadedPerformance> = compact
      ? api.listSettlementPoints(from, to, controller.signal).then(points => ({ key: performanceKey, points }))
      : api.getSettlementPerformance({ from, to, workerId: requestWorkerId,
          projectName: requestProjectName, subtaskName: requestSubtaskName, stage: requestStage,
        }, controller.signal).then(data => ({ key: performanceKey, data }));
    pending.then(result => {
      if (!controller.signal.aborted) setLoadedPerformance(result);
    }).catch((error: unknown) => {
      if (!controller.signal.aborted) setLoadError(error instanceof Error ? error.message : "성과 데이터를 불러오지 못했습니다.");
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    // Stop superseded HTTP requests, and also guard resolutions racing with abort.
    return () => controller.abort();
  }, [rangeEnd, rangeStart, requestWorkerId, requestProjectName, requestSubtaskName, requestStage, performanceKey, compact]);
  const records = useMemo<PerformanceRecord[]>(() => (performance?.days ?? []).map(day => ({
    id: day.date, workerId: selectedWorkerId, workerName: "", projectName: "", subtaskName: "", stage: null,
    completedAt: `${day.date}T00:00:00+09:00`, completedMs: performanceDateMs(day.date),
    amount: day.amount, normal: day.quantity, unitPrice: 0, recordCount: day.recordCount,
  })), [performance, selectedWorkerId]);
  const visibleRecords = useMemo(() => {
    const start = buckets[0]?.start ?? 0;
    const end = buckets.at(-1)?.end ?? 0;
    return records.filter((record) => record.completedMs >= start && record.completedMs < end && (selectedWorkerId === "all" || record.workerId === selectedWorkerId));
  }, [buckets, records, selectedWorkerId]);
  /* 좁은 카드에서는 시계가 아니라 자료를 따라 칸을 나눈다 — 밤과 휴일이 전체 시간의 3분의 2라
     시각으로 자르면 막대가 듬성듬성해진다. 시세 차트가 휴장을 건너뛰는 것과 같은 규칙이다. */
  /* 좁은 카드는 칸을 서른 개로 묶는다 — 레퍼의 그림이 30일을 점 서른 개로 그린다. 하루에
     한 점씩 180개를 찍으면 선이 물결이 아니라 잡음이 되어, 봉우리가 어디인지 읽히지 않는다.
     달력 칸을 묶으므로 각 점은 여전히 "며칠부터 며칠까지"이고, 짚으면 그 구간이 뜬다. */
  const shown = useMemo(() => {
    const slots = 30;
    if (!compact || compactScale || buckets.length <= slots) return buckets;
    const size = buckets.length / slots;
    return Array.from({ length: slots }, (_, index) => {
      const first = buckets[Math.floor(index * size)];
      const last = buckets[Math.min(buckets.length - 1, Math.floor((index + 1) * size) - 1)] ?? first;
      return { start: first.start, end: last.end, label: performanceShortDate(first.start) };
    });
  }, [buckets, compact]);
  /* 칸마다 전체 기록을 훑던 것을 한 번 훑는 것으로 바꾼다. 칸이 180개이고 기록이 3천 줄이면
     한 번 그릴 때마다 50만 번을 견주었고, 그 계산이 memo 없이 렌더마다 돌아 그래프에 얹기만
     해도 화면이 굳었다. 칸은 시간 순이므로 기록을 한 번 정렬해 두고 손가락 하나로 따라간다. */
  const chartValues = useMemo(() => {
    const totals = new Array<number>(shown.length).fill(0);
    const sorted = [...visibleRecords].sort((left, right) => left.completedMs - right.completedMs);
    let slot = 0;
    for (const record of sorted) {
      while (slot < shown.length && record.completedMs >= shown[slot].end) slot += 1;
      if (slot >= shown.length) break;
      if (record.completedMs >= shown[slot].start) totals[slot] += record.amount;
    }
    return totals;
  }, [shown, visibleRecords]);
  /* 같은 칸의 작업 개수. 돈 옆에 나란히 그려야 "많이 해서 번 날"과 "비싼 것을 한 날"이
     갈린다 — 금액만 보면 둘이 같은 봉우리로 보인다. */
  const chartCounts = useMemo(() => {
    const totals = new Array<number>(shown.length).fill(0);
    const sorted = [...visibleRecords].sort((left, right) => left.completedMs - right.completedMs);
    let slot = 0;
    for (const record of sorted) {
      while (slot < shown.length && record.completedMs >= shown[slot].end) slot += 1;
      if (slot >= shown.length) break;
      if (record.completedMs >= shown[slot].start) totals[slot] += record.normal;
    }
    return totals;
  }, [shown, visibleRecords]);
  /* 같은 길이의 바로 앞 기간. 뒤에 연회색으로 깔아 두면 선을 긋지 않고도 "지난번보다 어떤가"가
     보인다 — 막대는 구간마다 따로 센 값이라 이어 그릴 수 없지만, 두 벌을 겹쳐 세우는 것은
     각각을 그대로 둔 채 견주는 일이다. */
  const compareValues = useMemo(() => {
    if (!shown.length) return [];
    const span = (shown.at(-1)!.end - shown[0].start) || 1;
    const totals = new Array<number>(shown.length).fill(0);
    const sorted = [...records].filter(record => selectedWorkerId === "all" || record.workerId === selectedWorkerId)
      .sort((left, right) => left.completedMs - right.completedMs);
    let slot = 0;
    for (const record of sorted) {
      const at = record.completedMs + span;
      while (slot < shown.length && at >= shown[slot].end) slot += 1;
      if (slot >= shown.length) break;
      if (at >= shown[slot].start) totals[slot] += record.amount;
    }
    return totals;
  }, [shown, records, selectedWorkerId]);
  const totalAmount = useMemo(() => visibleRecords.reduce((sum, record) => sum + record.amount, 0), [visibleRecords]);
  /* 같은 기간의 작업 개수 합. 머리줄에서 돈과 나란히 선다. */
  const totalCount = useMemo(() => visibleRecords.reduce((sum, record) => sum + record.normal, 0), [visibleRecords]);
  // Ranking/worker choices are computed before the worker filter on the server.
  const ranking = performance?.workers ?? [];
  const people = ranking;
  const settlementCount = useMemo(() => visibleRecords.reduce((sum, record) => sum + record.recordCount, 0), [visibleRecords]);
  /* 로봇도 정산 줄을 남길 수 있다 — 사람만 뒤지면 고른 대상이 안 잡혀 머리줄이 "작업자 전체"로
     되돌아가고, 고른 적이 없는 것처럼 보인다. */
  const selectedWorker = data.workers.find((worker) => worker.id === selectedWorkerId);
  /* 한 사람을 고르면 이 줄이 그 사람의 기록표가 된다. 따로 표를 하나 더 두면 같은 사람을
     두 군데서 봐야 하고, 어느 쪽이 지금 고른 기간인지도 매번 확인해야 한다. */
  /* 아래 셋은 기간과 고른 사람이 정하는 값인데, memo 없이 두었더니 그래프에 손을 얹어
     짚은 칸이 바뀔 때마다 삼천 줄을 처음부터 다시 훑었다 — 값이 늦게 따라오던 이유다. */
  const workedDays = useMemo(
    () => new Set(visibleRecords.map(record => performanceDateKey(new Date(record.completedAt)))).size,
    [visibleRecords]);
  /* 달력 구간 평균은 "기록이 있는 구간"으로 나눈다. 기간 안의 쉰 주까지 세면 오래 쉴수록
     주급이 낮아 보이는데, 그건 그 사람이 한 주에 얼마를 버느냐와 다른 이야기다. */
  const spanStart = buckets[0]?.start ?? 0;
  const spanCounts = useMemo(() => {
    const seen = new Map<number, Set<number>>([[7, new Set()], [14, new Set()], [28, new Set()]]);
    for (const record of visibleRecords) {
      for (const [days, slots] of seen) slots.add(Math.floor((record.completedMs - spanStart) / (days * dayMs)));
    }
    return new Map([...seen].map(([days, slots]) => [days, slots.size]));
  }, [visibleRecords, spanStart]);
  const spanCount = (days: number) => spanCounts.get(days) ?? 0;
  /* 근무시간은 시간으로 셈하는 작업자에게만 있다 — 도급은 인형을 받아 가 집에서 만들어 오므로
     회사가 아는 것은 개수뿐이다. 없는 사람에게는 "-"를 둔다. 지어내지 않는다. */
  const { workMinutes, recentMinutes } = useMemo(() => {
    const weekAgo = new Date(Date.now() - 7 * dayMs).toISOString().slice(0, 10);
    let inRange = 0, recent = 0;
    for (const record of data.partTimeWorkRecords) {
      if (record.workerId !== selectedWorkerId) continue;
      if (!customRange || (record.workDate >= customRange.start && record.workDate <= customRange.end)) inRange += record.workMinutes;
      if (record.workDate >= weekAgo) recent += record.workMinutes;
    }
    return { workMinutes: inRange, recentMinutes: recent };
  }, [data.partTimeWorkRecords, selectedWorkerId, customRange?.start, customRange?.end]);
  const hours = (minutes: number) => `${(minutes / 60).toLocaleString("ko-KR", { maximumFractionDigits: 1 })}시간`;
  /* 평균마다 그 눈금의 차트를 연다. 숫자는 수준을 말하고 차트는 그 수준이 어떻게 쌓였는지를
     말한다 — 주 평균이 얼마인지와, 그게 한 주에 몰렸는지 고르게 벌렸는지는 다른 사실이다.
     여는 자리가 눈금을 정하므로 창 안에서 또 고를 필요가 없다. */
  /* 두 화면이 같은 항목을 쓴다. 전체를 볼 때는 공장 전체가 한 사람인 셈이다 — 그래야 ⓘ의
     뜻이 두 화면에서 같아지고, "총 성과"에 일 단위 차트가 붙는 이상한 짝도 없어진다. */
  /* 넷이 한 가족이라 말투를 맞춘다. 나누는 것은 달력 날이 아니라 실제로 일한 날·구간이다.
     나눈 개수를 값 옆에 적는다 — 한 주만 일한 사람은 주 평균이 총액과 같은 숫자가 되는데,
     "(1주)"가 없으면 그게 왜 같은지 알 수 없어 수상해 보인다. 숨기지 않는 이유는 사람마다
     줄이 달라지면 두 사람을 나란히 놓고 볼 수 없기 때문이다. */
  const averages = ([
    { label: "일 평균", days: 1, unit: "일", only: "하루만", chart: "day" },
    { label: "주 평균", days: 7, unit: "주", only: "한 주만", chart: "week" },
    { label: "2주 평균", days: 14, unit: "번", only: "한 번만", chart: "biweek" },
    { label: "4주 평균", days: 28, unit: "번", only: "한 번만", chart: "week4" },
  ] as const).map(({ label, days, unit, only, chart }) => {
    const spans = days === 1 ? workedDays : spanCount(days);
    return {
      label,
      value: spans ? money(Math.round(totalAmount / spans)) : "-",
      /* 나눈 수를 늘 적으면 "(5일)" 같은 토막말이 줄마다 붙어 무슨 소린지 알 수 없다. 나눌
         것이 하나뿐일 때만 적는다 — 그때만 평균이 총액과 같은 숫자가 되어 수상해 보이고,
         그 이유가 바로 "한 주만 일해서"이기 때문이다. */
      note: spans === 1 ? only : undefined,
      hint: spans ? `${money(totalAmount)} ÷ ${spans}${unit}` : undefined,
      /* 막대 하나짜리 그림은 그림이 아니라 숫자 하나다. 그때는 문을 열어 둘 이유가 없고,
         옆의 "(1주)"가 왜 없는지도 말해 준다. */
      chart: spans > 1 ? (chart as PerformanceGranularity) : undefined,
    };
  });
  const summary: Array<{ label: string; value: string; note?: string; hint?: string; chart?: PerformanceGranularity; rank?: boolean }> = selectedWorker ? [
    { label: `${selectedWorker.displayedName} 총 성과`, value: money(totalAmount) },
    { label: "작업일", value: `${workedDays.toLocaleString()}일` },
    ...averages,
    { label: "근무시간", value: workMinutes ? hours(workMinutes) : "-" },
    /* 시급은 번 돈과 일한 시간이 둘 다 있어야 나온다. 한쪽이 비어 있는데 0원이라고 적으면
       "한 시간에 0원"이라는 없는 사실이 된다. */
    { label: "시급", value: workMinutes && totalAmount ? money(Math.round(totalAmount / (workMinutes / 60))) : "-" },
    { label: "최근 1주 근무", value: recentMinutes ? hours(recentMinutes) : "-" },
  ] : [
    /* 최고 성과자와 작업자 평균은 뺐다. 순위 창이 1등을 맨 위에 이미 보여 주므로 같은 말을
       두 번 하고 있었고, 사람끼리의 비교는 전부 그 창이 맡는다. */
    { label: "총 성과", value: money(totalAmount) },
    /* 이 수는 그 기간에 정산할 돈이 생긴 사람의 수다. "정산"을 붙이지 않는 것은 이 카드가
       처음부터 정산 이야기뿐이라 그 말이 모든 항목에 똑같이 걸리기 때문이다. 바로 옆 총
       성과가 몇 사람의 몫인지를 말하고, 그 사람들의 순위를 ⓘ가 연다 — 평균 옆의 ⓘ가 그
       눈금 차트를 열듯, 숫자마다 자기 상세로 가는 문이 하나씩이다. */
    { label: "작업자", value: `${ranking.length}명`, rank: true },
    ...averages,
  ];
  /* "집계단위별 세부내역" 카드는 뺐다. 그 표의 행은 위 정산 표가 같은 기간 필터로 이미
     보여 주는 바로 그 행이었다 — performanceRecordsFromSettlements가 정산 레코드를 그대로
     옮겨 담는다. 같은 줄을 한 화면에서 두 번 보여 주면 둘이 다른 것인 줄 알고 맞춰 보게 된다. */
  /* 고른 범위보다 큰 눈금은 막대를 한두 개만 남겨 차트가 아니라 숫자 하나가 된다. 예컨대
     한 달을 보는데 4주 단위면 막대가 하나다. 그런 눈금은 아예 내보이지 않는다. */
  const rangeDays = customRange
    ? Math.round((Date.parse(customRange.end) - Date.parse(customRange.start)) / dayMs) + 1
    : 183;
  const usableGranularity = (value: PerformanceGranularity) => performanceGranularityDays[value] * 2 <= rangeDays;
  useEffect(() => { if (!usableGranularity(granularity)) setGranularity("day"); }, [rangeDays]);

  if (compact) return <div className={`performance-compact${reflectTitle ? " performance-compact--reflect" : ""}`}>
    {/* 2026-10-19 밤 지윤: 성과는 Claude Reflect 그대로 — 제목 아래 기준 한 문장, 그 아래 숫자 줄(합계·건수). */}
    {reflectTitle && <div className="performance-reflect-head">
      <h3>{reflectTitle}</h3>
      {/* Reflect 의 「Based on your conversations in Claude chat.」 자리 — 무엇을 세었는지. 숫자는 아래 숫자 줄이 말한다(2026-10-19 밤 지윤). */}
      {/* 2026-10-19 밤 지윤: 큰 숫자 타일을 걷고 이 한 문장이 합계를 말한다(정산된 기록 기준). */}
      {/* 「전체 작업자 N명의 최근 6개월 총 성과는 …, 평균 성과는 …입니다.」 — N과 평균은 그 기간에 성과가 난 사람 기준(드롭다운 순위와 같은 명단). */}
      {/* 줄글 대신 숫자 셋을 나란히(2026-10-21 지윤 「성과 얼마얼마 탁 눈에 보이게, 세 요소 정도가 줄글보다 낫다」).
          전체: 작업자 수 · 총 성과 · 1인 평균 · 정산 건수(2026-10-21 「전체에도 정산 건수」) / 한 사람: 작업자 이름 · 총 성과 · 정산 건수(순위 대신 이름, 2026-10-21 지윤). 사람 수·평균·순위는 그 기간에 성과가 난 사람 기준(드롭다운 순위와 같은 명단). */}
      {(() => {
        const earners = people.filter(worker => worker.amount > 0);
        const stats: Array<[string, string]> = selectedWorker
          ? [["작업자", selectedWorker.displayedName], ["총 성과", money(totalAmount)], ["정산 건수", `${settlementCount.toLocaleString("ko-KR")}건`]]
          : [["작업자", `${earners.length.toLocaleString("ko-KR")}명`], ["총 성과", money(totalAmount)], ["1인 평균", money(earners.length ? Math.round(totalAmount / earners.length) : 0)], ["정산 건수", `${settlementCount.toLocaleString("ko-KR")}건`]];
        /* 차례는 누구 → 얼마(2026-10-21 지윤 「작업자가 처음이 맞지 않나」). */
        /* 금액은 원 단위 그대로 — 「만원」으로 줄이면 1만4천원이 「1만원」이 되어 숫자가 틀려 보였다. 셋째 칸 폭에 열세 자리까지 들어간다. */
        return <dl className="performance-reflect-stats" aria-busy={loading}>
          {stats.map(([name, value]) => <div key={name}><dt>{name}</dt><dd title={value}>{loading ? "—" : value}</dd></div>)}
        </dl>;
      })()}
    </div>}
    {/* 기간 칩이 맨 위다 — 정산 예정·내역 칸이 바로 아래 줄에서 칩으로 시작하므로, 성과 칸만
        글자 줄로 시작하면 세 칸이 같은 자리에서 다른 모양으로 열린다. 한때 그림 아래에
        두었는데(시세 화면의 자리), 그건 이 칸이 카드 하나로 떠 있을 때의 이야기였다. */}
    {/* 기간 칩과 작업자 거르개가 한 줄이다 — 둘 다 "이 그림이 무엇을 보여 주나"를 정하는
        같은 층의 조건이라, 자리를 나누면 하나를 바꿀 때마다 다른 하나를 어디서 바꿨는지
        찾게 된다. 기간은 왼쪽, 사람은 오른쪽 끝. */}
    {/* 머리줄은 레퍼의 차트 머리 그대로다 — 왼쪽은 "무엇의 얼마인가"(고른 사람과 금액),
        오른쪽은 "언제까지"(기간). 집계단위 고르개는 걷었다: 눈금을 고르는 일은 기간이 이미
        정하고 있어서, 같은 질문에 손잡이가 둘이었다. */}
    {/* 서랍은 520이라 타일이 둘까지만 선다 — 고르개는 머리줄로 돌아온다. 왼쪽이 "누구를
        보나", 오른쪽이 "언제까지"다. 레퍼의 카드도 머리에 칩 줄을 두고 타일은 둘만 세운다. */}
    <div className="performance-compact-head">
      <span className="settlement-badge">
        {/* 자리를 재기 전 첫 장면에는 목록을 숨긴다 — toggle 은 펼친 뒤에 오므로, 그 사이 목록이 기본 자리에서 넓게 펼쳐져
            설정 창 밖으로 삐져나왔다가 제자리로 튀었다(2026-10-20 지윤). 닫으면 자리를 잊어 다음에도 재고 나서 보인다. */}
        <details className="performance-people" ref={peopleRef} onToggle={event => event.currentTarget.open ? openPeople() : setListStyle(undefined)}>
          <summary>{selectedWorker ? selectedWorker.displayedName : "작업자 전체"}<ChevronDown className="performance-people-chevron" size={16} aria-hidden="true" /></summary>
          <ol className="performance-people-list" ref={listRef} style={listStyle ?? { visibility: "hidden" }}>
            <li><button type="button" aria-selected={!selectedWorker} onClick={() => { onSelectWorker?.("all"); peopleRef.current?.removeAttribute("open"); }}><strong>전체</strong>{!selectedWorker && <Check className="performance-people-check" size={20} aria-hidden="true" />}</button></li>
            {people.map((worker, index) => <li key={worker.id}>
              <button type="button" aria-selected={selectedWorker?.id === worker.id} onClick={() => { onSelectWorker?.(worker.id); peopleRef.current?.removeAttribute("open"); }}>
                <b>{index + 1}</b><strong title={worker.name}>{worker.name}</strong>
                <span className="performance-people-track" aria-hidden="true"><i style={{ width: `${people[0]?.amount ? Math.max(4, worker.amount / people[0].amount * 100) : 0}%` }} /></span>
                <em>{money(worker.amount)}</em>
                {selectedWorker?.id === worker.id && <Check className="performance-people-check" size={20} aria-hidden="true" />}
              </button>
            </li>)}
          </ol>
        </details>
      </span>
      <div className="performance-compact-scale" role="group" aria-label="기간">{scaleExtra}</div>
    </div>
    {/* 칩 줄 아래 한 줄 — 레퍼가 "Read-only demo…"를 두는 자리다. 이 카드가 무엇을 세고
        무엇을 안 세는지 한 번 적어 두면, 숫자가 기대와 다를 때 어디를 볼지가 정해진다. */}
    {/* 요약 타일. 레퍼가 그림 위에 다섯 칸을 세우는 자리이고 치수도 잰 값 그대로다 —
        라벨 11·500 흐림 위에 값 28·500. 그림이 "어떻게 흘렀나"를 말하면 이 줄은 "그래서
        얼마인가"를 말한다: 그림만 있을 때는 봉우리는 보이는데 합계를 눈으로 더해야 했다. */}
    {/* 타일은 둘이다 — 레퍼의 카드가 Events·Visitors 둘을 두는 자리이고, 520 폭에서 28px
        숫자가 들어가는 칸은 둘이 한계다. 셋째부터는 넓은 자리를 채우려고 넣는 값이 된다:
        건당 평균은 이 둘을 나누면 나오고, 작업일은 그림의 가로축이 이미 말한다. */}
    {!reflectTitle && <dl className="performance-compact-tiles">
      <div>
        <dt>성과</dt>
        {/* 짧게 적는다 — 레퍼의 숫자는 네댓 자(3,370)라 28px이 들어가지만 원 단위 금액은
            열두 자다. 28을 지키려면 자릿수를 줄여야 하고, 그림의 세로 눈금이 이미 같은
            단위(만)를 쓰므로 둘이 같은 자로 읽힌다. 정확한 원 단위는 정산 표가 들고 있다. */}
        <dd title={money(totalAmount)}>{shortMoney(totalAmount)}</dd>
        <p>기간 합계</p>
      </div>
      {/* 설정 > 성과(Reflect)에서는 건수를 두지 않는다 — 정산 줄 수는 판단에 쓰이지 않는 장부 숫자다(2026-10-19 밤 지윤). */}
      {!reflectTitle && <div>
        <dt>정산 건수</dt>
        <dd>{settlementCount.toLocaleString("ko-KR")}건</dd>
        <p>이 기간에 정산된 줄</p>
      </div>}
    </dl>}
    {/* 그림의 이름. 레퍼가 그림 위에 "Volume" 한 줄을 두는 자리다 — 없으면 타일과 그림이
        한 덩어리로 붙어, 위가 합계고 아래가 흐름이라는 것이 자리로만 말해진다. */}
    <div className="performance-compact-section-row">
      <h3 className="performance-compact-section">{sectionTitle}</h3>
      {sectionTail}
    </div>
    {!hideChart && <div className="performance-compact-chart">
      {loading ? <EmptyState>불러오는 중…</EmptyState>
        : loadError ? <EmptyState>{loadError}</EmptyState>
        : visibleRecords.length ? <PerformanceChart buckets={shown} values={chartValues} counts={chartCounts} compare={compareValues} individual={!!selectedWorker}
            picked={picked} onPick={index => setPicked(index < 0 ? null : index)} />
        : <EmptyState>이 기간에 완료된 성과가 없습니다.</EmptyState>}
    </div>}
    {/* 최근 정산 세 줄은 걷었다. 세 줄은 전체도 아니고 찾는 사람도 아닌 임의 표본이라
       답하는 질문이 없었고, 세 줄의 둘째 칸이 모두 "프로젝트"로 같아 정보 없이 자리만
       먹었다. 0원 줄은 왜 0인지 말할 자리가 이 카드에 없어 사고처럼 읽혔다.
       이 카드가 답하는 것은 "요즘 얼마나 벌고 있나"이고, 그 답은 위의 합계·건수·추이가
       이미 한다. 줄 단위로 볼 일은 아래 "정산"이 여는 장부가 제대로 한다. */}
    {/* 순위 창은 좁은 카드에서도 열려야 한다 — ⓘ가 여는 문인데 이 분기가 먼저 돌아가면서
        창 자체가 안 그려지고 있었다. */}
    {!selectedWorker && rankingOpen && <Modal title="작업자 성과 순위" className="performance-ranking-dialog" onClose={() => setRankingOpen(false)} restoreFocus={() => rankButton.current}>
      {ranking.length ? <ol className="performance-rank-grid">{ranking.map((worker, index) => <li key={worker.id}><b>{index + 1}</b><div><strong title={worker.name}>{worker.name}</strong><span className="performance-rank-track" aria-hidden="true"><i style={{ width: `${ranking[0]?.amount ? Math.max(4, worker.amount / ranking[0].amount * 100) : 0}%` }} /></span></div><span>{money(worker.amount)}</span></li>)}</ol> : <EmptyState>순위를 계산할 성과 데이터가 없습니다.</EmptyState>}
    </Modal>}
  </div>;

  return <>
    <div className="performance-headline">
      {/* 이 줄이 무엇의 합인지 먼저 적는다. 범위는 아래 표의 날짜 열이 정하는데, 그 연결이
          화면에 없으면 숫자만 남아 "언제 것인지"를 매번 되묻게 된다. 아무것도 고르지 않았으면
          전체이고, 그것도 적어 준다 — 비어 있는 것과 전체는 다른 말이다. */}
      <p className="performance-period">{range ? `${range.from} ~ ${range.to}` : "전체 기간"}</p>
      {/* 이 줄이 카드의 답이다. 접든 펴든 늘 보인다. */}
      <p className="performance-summary" aria-label="성과 요약">{summary.map(item =>
        <span key={item.label}>{item.label}
          <strong title={item.hint ?? item.value} data-empty={item.value === "-" || undefined}>{item.value}</strong>
          {item.note && <i>({item.note})</i>}
          {item.rank && ranking.length > 0 && <button ref={rankButton} type="button" className="performance-rank-open"
            aria-label="작업자 성과 순위 보기" data-testid="open-performance-ranking" onClick={() => setRankingOpen(true)}>ⓘ</button>}
          {item.chart && <button type="button" className="performance-rank-open"
            aria-label={`${item.label} 차트 보기`} data-testid={`open-chart-${item.chart}`}
            onClick={event => { chartButton.current = event.currentTarget; setGranularity(item.chart!); setChartOpen(true); }}>ⓘ</button>}
        </span>)}</p>
    </div>
    {/* 차트는 창으로 뺀다. 카드 안에서 펼치면 그때마다 아래 일감(고르기·이체·표)이 한 화면씩
        밀렸다. 이 카드 안에는 이미 같은 규칙이 있다 — 최고 성과자 옆 ⓘ가 순위를 창으로 연다.
        요약 줄의 숫자를 더 알고 싶으면 창을 연다. */}
    {chartOpen && <Modal title={`${performanceGranularityLabels[granularity]} 단위 성과`} className="performance-chart-dialog" onClose={() => setChartOpen(false)} restoreFocus={() => chartButton.current}>
      <div className="performance-chart-card">
        {/* 눈금을 고치는 칸은 두지 않는다. 어느 숫자의 ⓘ로 들어왔는지가 이미 눈금을 정했고,
            창 제목이 그것을 말한다. 안에 또 두면 같은 일을 두 곳에서 하게 되고, 바꾸고 나면
            제목과 고른 값 중 어느 쪽이 맞는지 한 번 더 확인해야 한다. */}
        {loading ? <div className="performance-chart-empty"><EmptyState>성과 데이터를 불러오는 중입니다.</EmptyState></div> : loadError ? <div className="performance-chart-empty"><EmptyState>{loadError}</EmptyState></div> : visibleRecords.length ? <PerformanceChart buckets={buckets} values={chartValues} individual={!!selectedWorker} /> : <div className="performance-chart-empty"><EmptyState>선택한 기간에 완료된 성과 데이터가 없습니다.</EmptyState></div>}
      </div>
    </Modal>}
    {!selectedWorker && rankingOpen && <Modal title="작업자 성과 순위" className="performance-ranking-dialog" onClose={() => setRankingOpen(false)} restoreFocus={() => rankButton.current}>
      {ranking.length ? <ol className="performance-rank-grid">{ranking.map((worker, index) => <li key={worker.id}><b>{index + 1}</b><div><strong title={worker.name}>{worker.name}</strong><span className="performance-rank-track" aria-hidden="true"><i style={{ width: `${ranking[0]?.amount ? Math.max(4, worker.amount / ranking[0].amount * 100) : 0}%` }} /></span></div><span>{money(worker.amount)}</span></li>)}</ol> : <EmptyState>순위를 계산할 성과 데이터가 없습니다.</EmptyState>}
    </Modal>}
  </>;
}
